import { useCallback, useEffect, useMemo, useState } from "react";
import { ListChecks, Sparkles, X } from "lucide-react";
import { ModuleDrawerAdd } from "@/components/ModuleDrawerAdd";
import { openHubAi } from "@/lib/hubAssist";
import { matchesHubSearch, useHubSearchQuery } from "@/lib/hubSearch";
import { fetchMeetings } from "@/lib/meetingsConnect";
import { peekModuleCache, writeModuleCache } from "@/lib/moduleCache";
import { useLiveRefresh } from "@/lib/useLiveRefresh";
import {
  meetingStatusLabel,
  meetingSystemLabel,
  type HubMeeting,
} from "@/meetings/model";

const MEETINGS_CACHE_KEY = "meetings";
const MEETINGS_REFRESH_MS = 120_000;

function formatWhen(start: string | null, end: string | null): string {
  if (!start) return "";
  const a = new Date(start);
  if (Number.isNaN(a.getTime())) return start;
  const date = a.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
  if (!end) return date;
  const b = new Date(end);
  if (Number.isNaN(b.getTime())) return date;
  const time = b.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return `${date} – ${time}`;
}

/** What Hub AI gets about the meeting a row's button was pressed on. */
function meetingFocus(meeting: HubMeeting) {
  return {
    kind: "meeting",
    id: meeting.id,
    title: meeting.title,
    start: meeting.start,
    end: meeting.end,
    status: meeting.status,
    system: meeting.system,
    attendees: (meeting.attendees || []).slice(0, 25),
    location: meeting.location || "",
  };
}

function askAboutMeeting(meeting: HubMeeting, ask: "prep" | "followups") {
  const prompt =
    ask === "prep"
      ? `Prep me for "${meeting.title}"`
      : `What follow-ups should I take from "${meeting.title}"? Suggest tasks.`;
  openHubAi({ prompt, send: true, fresh: true, focus: meetingFocus(meeting) });
}

function MeetingRow({ meeting }: { meeting: HubMeeting }) {
  const people = (meeting.attendees || []).slice(0, 3).join(", ");
  const followUps = meeting.status !== "upcoming";
  return (
    <li className="meeting-row">
      <div className="meeting-copy">
        <strong>{meeting.title}</strong>
        <p className="muted tight">{formatWhen(meeting.start, meeting.end)}</p>
        <p className="meeting-meta">
          <span className={`pill ${meeting.status === "live" ? "pill-connected" : ""}`}>
            {meetingStatusLabel(meeting.status)}
          </span>
          <span className="muted">{meetingSystemLabel(meeting.system)}</span>
          {meeting.accountEmail ? <span className="muted">{meeting.accountEmail}</span> : null}
        </p>
        {people ? <p className="muted tight">{people}</p> : null}
      </div>
      <div className="meeting-actions">
        <button
          type="button"
          className="secondary-btn meeting-ai-btn"
          title={followUps ? "Ask Hub AI for follow-up tasks" : "Ask Hub AI to prep you"}
          onClick={() => askAboutMeeting(meeting, followUps ? "followups" : "prep")}
        >
          {followUps ? <ListChecks size={14} aria-hidden /> : <Sparkles size={14} aria-hidden />}
          {followUps ? "Follow-ups" : "Prep"}
        </button>
        {meeting.joinUrl ? (
          <a className="primary-btn meeting-join" href={meeting.joinUrl} target="_blank" rel="noreferrer">
            Join
          </a>
        ) : null}
      </div>
    </li>
  );
}

export function MeetingsPage() {
  const [meetings, setMeetings] = useState<HubMeeting[]>(
    () => peekModuleCache<HubMeeting[]>(MEETINGS_CACHE_KEY) ?? [],
  );
  const [error, setError] = useState("");
  const [meetingsDrawerOpen, setMeetingsDrawerOpen] = useState(false);
  const searchQuery = useHubSearchQuery("meetings").trim();
  const shownMeetings = useMemo(
    () =>
      meetings.filter((meeting) =>
        matchesHubSearch(
          searchQuery,
          meeting.title,
          meeting.location,
          meeting.attendees,
          meeting.accountEmail,
          meetingSystemLabel(meeting.system),
        ),
      ),
    [meetings, searchQuery],
  );

  const loadMeetings = useCallback(async () => {
    try {
      const next = await fetchMeetings();
      setMeetings(next);
      setError("");
      void writeModuleCache(MEETINGS_CACHE_KEY, next, { mirror: true });
    } catch (err) {
      if (!peekModuleCache(MEETINGS_CACHE_KEY)) {
        setError(err instanceof Error ? err.message : "Meetings unavailable.");
      }
    }
  }, []);

  useEffect(() => {
    void loadMeetings();
  }, [loadMeetings]);

  useLiveRefresh(() => void loadMeetings(), MEETINGS_REFRESH_MS);

  useEffect(() => {
    function openDrawer() {
      setMeetingsDrawerOpen(true);
    }
    window.addEventListener("atw-hub-open-meetings-drawer", openDrawer);
    return () => window.removeEventListener("atw-hub-open-meetings-drawer", openDrawer);
  }, []);

  return (
    <div className="page-pad">
      <div className="tasks-head">
        <div>
          <h1>Meetings</h1>
          <p className="muted">Google Meet and Zoom, in one Hub list</p>
        </div>
        <button
          type="button"
          className="secondary-btn tasks-ai-btn"
          onClick={() => openHubAi({ prompt: "Which of my upcoming meetings need prep, and what should I bring?", send: true })}
        >
          <Sparkles size={15} aria-hidden /> Ask Hub AI
        </button>
      </div>
      {error ? <p className="banner-error">{error}</p> : null}
      <ul className="simple-list">
        {shownMeetings.map((meeting) => (
          <MeetingRow key={meeting.id} meeting={meeting} />
        ))}
        {searchQuery && meetings.length > 0 && !shownMeetings.length ? (
          <li className="muted">No meetings match “{searchQuery}”.</li>
        ) : null}
        {!meetings.length && !error ? (
          <li className="muted">No upcoming meetings. Connect Google or Zoom to load them.</li>
        ) : null}
      </ul>

      {meetingsDrawerOpen ? (
        <div
          className="module-slide-scrim phone-only"
          onClick={() => setMeetingsDrawerOpen(false)}
        >
          <aside
            className="module-slide-drawer"
            aria-label="Meetings"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="module-slide-drawer-head">
              <strong>Meetings</strong>
              <button
                type="button"
                className="module-slide-close"
                aria-label="Close Meetings menu"
                onClick={() => setMeetingsDrawerOpen(false)}
              >
                <X size={16} />
              </button>
            </div>
            <div className="module-drawer-body">
              <p className="mail-nav-kicker">Upcoming</p>
              <ul className="simple-list">
                {meetings.map((meeting) => (
                  <li key={`drawer-${meeting.id}`}>
                    <div>
                      <strong>{meeting.title}</strong>
                      <p className="muted tight">
                        {meetingSystemLabel(meeting.system)}
                        {meeting.start ? ` · ${formatWhen(meeting.start, meeting.end)}` : ""}
                      </p>
                    </div>
                  </li>
                ))}
                {!meetings.length ? <li className="muted">No meetings loaded.</li> : null}
              </ul>
            </div>
            <ModuleDrawerAdd
              to="/settings/connections/meetings"
              label="Connect meetings"
              onNavigate={() => setMeetingsDrawerOpen(false)}
            />
          </aside>
        </div>
      ) : null}
    </div>
  );
}
